import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Cookie, Scale, ShieldCheck, FileText, type LucideIcon } from "lucide-react";
import { detectLangFromPath } from "@/lib/site";
import { useLegalPage } from "@/lib/useLegalPage";
import { openCookiePreferences } from "@/components/CookieConsent";

interface Props {
  slug: "mentions-legales" | "confidentialite" | "cookies" | "cgv";
  /** Optional fallback static content used if DB row is missing/loading. */
  fallback?: {
    eyebrow: string;
    title: string;
    intro: string;
    sections: Array<{ title: string; body: string }>;
  };
}

const ICON_BY_SLUG: Record<Props["slug"], LucideIcon> = {
  "mentions-legales": Scale,
  confidentialite: ShieldCheck,
  cookies: Cookie,
  cgv: FileText,
};

export function DynamicLegalPage({ slug, fallback }: Props) {
  const location = useLocation();
  const lang = detectLangFromPath(location.pathname);
  const { t } = useTranslation();
  const { data, isLoading } = useLegalPage(slug, lang);
  const Icon = ICON_BY_SLUG[slug];

  const content = data ?? fallback;
  const isCookies = slug === "cookies";

  if (isLoading && !content) {
    return (
      <div className="container-page py-20 md:py-28">
        <div className="mx-auto max-w-3xl">
          <div className="h-6 w-40 animate-pulse rounded bg-foreground/10" />
          <div className="mt-6 h-12 w-3/4 animate-pulse rounded bg-foreground/10" />
          <div className="mt-4 h-4 w-full animate-pulse rounded bg-foreground/10" />
        </div>
      </div>
    );
  }

  if (!content) return null;

  return (
    <div className="container-page py-20 md:py-28">
      <div className="mx-auto max-w-3xl">
        {content.eyebrow && (
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--border-strong)] px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground/70">
            <Icon size={13} /> {content.eyebrow}
          </div>
        )}
        <h1 className="font-display text-4xl font-bold tracking-[-0.03em] text-foreground sm:text-5xl">
          {content.title}
        </h1>
        {content.intro && (
          <p className="mt-4 text-lg leading-relaxed text-foreground/70">{content.intro}</p>
        )}

        <div className="mt-10 space-y-8">
          {content.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-display text-xl font-semibold tracking-tight text-foreground">
                {s.title}
              </h2>
              <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-foreground/75">
                {s.body}
              </p>
            </section>
          ))}
        </div>

        {isCookies && (
          <div className="mt-12 rounded-2xl border border-[var(--border-strong)] bg-background p-6">
            <h2 className="font-display text-base font-semibold tracking-tight text-foreground">
              {t("cookies.page.manageTitle")}
            </h2>
            <p className="mt-2 text-sm text-foreground/70">{t("cookies.page.manageBody")}</p>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="btn-ink mt-4 inline-flex items-center justify-center rounded-full px-5 py-2 text-xs font-medium uppercase tracking-[0.12em]"
            >
              {t("cookies.page.manageCta")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
